import { useState } from "react";
import * as Icons from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const FORMATS = [["pdf", "PDF", "FileText", "pdf"], ["excel", "Excel", "FileSpreadsheet", "xlsx"], ["csv", "CSV", "FileDown", "csv"]];

function Choice({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-1.5 transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-input hover:bg-secondary"}`}>
      {children}
    </button>
  );
}

export function ExportButton({ report, title, filters = {}, label = "Export", testid = "export-open-btn" }) {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState("pdf");
  const [range, setRange] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const active = Object.entries(filters).filter(([, v]) => v && v !== "all");

  const run = async () => {
    if (range === "range" && !from && !to) return toast.error("Pick a From or To date");
    if (range === "range" && from && to && from > to) return toast.error("From date must be before To date");
    setBusy(true);
    try {
      const params = { format, ...Object.fromEntries(active) };
      if (range === "range") { if (from) params.date_from = from; if (to) params.date_to = to; }
      const r = await api.get(`/reports/${report}`, { params, responseType: "blob" });
      const ext = FORMATS.find((f) => f[0] === format)[3];
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = `${report}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click(); URL.revokeObjectURL(u);
      toast.success("Export downloaded");
      setOpen(false);
    } catch (e) {
      let msg = "Export failed";
      try { msg = formatApiError(JSON.parse(await e.response?.data?.text())?.detail) || msg; } catch { /* blob not json */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" className="gap-1.5" onClick={() => setOpen(true)} data-testid={testid}><Icons.Download className="h-4 w-4" />{label}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Export {title}</DialogTitle>
            <DialogDescription>Choose a format and the records to include.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="overline text-muted-foreground">Format</Label>
              <div className="flex gap-2">
                {FORMATS.map(([k, l, ic]) => { const I = Icons[ic]; return <Choice key={k} active={format === k} onClick={() => setFormat(k)} testid={`export-format-${k}`}><I className="h-4 w-4" />{l}</Choice>; })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-muted-foreground">Records</Label>
              <div className="flex gap-2">
                <Choice active={range === "all"} onClick={() => setRange("all")} testid="export-range-all">All records</Choice>
                <Choice active={range === "range"} onClick={() => setRange("range")} testid="export-range-dates">Date range</Choice>
              </div>
              {range === "range" && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div><Label className="text-xs text-muted-foreground">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="export-date-from" /></div>
                  <div><Label className="text-xs text-muted-foreground">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="export-date-to" /></div>
                </div>
              )}
            </div>
            {active.length > 0 && (
              <p className="text-xs text-muted-foreground rounded-md bg-secondary/60 px-3 py-2" data-testid="export-active-filters">
                On-screen filters applied: {active.map(([k, v]) => `${k === "q" ? "search" : k.replace(/_/g, " ")}: ${v}`).join(" · ")}
              </p>
            )}
            <Button className="w-full gap-1.5" onClick={run} disabled={busy} data-testid="export-download-btn">
              {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Download className="h-4 w-4" />}Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
